import { ArrowDown, LinkExternal } from '@ui/components/icons';
import { isAddressInPrivacyPoolAllowlist } from '@ui/constants/allowlist';
import {
  ANYCOLLATERAL_PROGRAM_LINK,
  BLOG_LINK,
  DISCORD_LINK,
  DUNE_LINK,
  GIT_BOOK_AUDITS,
  GIT_BOOK_LINK,
  MONAD_BRIDGE_LINK,
  TELEGRAM_LINK,
  TERMS_CONDITION,
  X_LINK,
} from '@ui/constants/links';
import { ROUTES } from '@ui/constants/routes';
import { NextLinkWithLocale } from '@ui/lib/i18n';
import { Icon } from '@ui/lib/park/ui/icon';
import { Menu } from '@ui/lib/park/ui/menu';
import { Text } from '@ui/lib/park/ui/text';
import { useTranslation } from 'next-i18next';
import { css } from 'styled-system/css';
import {
  Box,
  BoxProps,
  Divider,
  Flex,
  HStack,
  styled,
} from 'styled-system/jsx';
import { useAccount } from 'wagmi';

export type MoreMenuIconKind =
  | 'stats'
  | 'bridge'
  | 'docs'
  | 'portfolio'
  | 'leaderboard'
  | 'help'
  | 'privacy'
  | 'blog'
  | 'anyCollateral';

const moreMenuLinkClass = css({
  display: 'grid',
  gridTemplateColumns: '20px 1fr',
  gap: '10px',
  alignItems: 'flex-start',
  width: '100%',
  minH: '58px',
  padding: '10px 12px !important',
  margin: '0 !important',
  border: '1px solid transparent',
  borderRadius: '2px',
  outline: 'none',
  transition:
    'background 160ms ease, border-color 160ms ease, color 160ms ease',
  _hover: {
    bg: 'v2.bg.surface1',
  },
  _focus: { outline: 'none' },
  _focusVisible: {
    outline: '1px solid',
    outlineColor: 'v2.border.focus',
    outlineOffset: '-1px',
  },
});

const footerLinkClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  color: 'v2.text.muted',
  outline: 'none',
  transition: 'color 160ms ease',
  _hover: {
    color: 'v2.text.primary',
  },
  _focus: { outline: 'none' },
  _focusVisible: {
    outline: '1px solid',
    outlineColor: 'v2.border.focus',
    outlineOffset: '2px',
  },
});

const socialLinkClass = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  opacity: 0.68,
  outline: 'none',
  transition: 'opacity 160ms ease, transform 160ms ease',
  _hover: {
    opacity: 1,
    transform: 'translateY(-1px)',
  },
  _focus: { outline: 'none' },
  _focusVisible: {
    outline: '1px solid',
    outlineColor: 'v2.border.focus',
    outlineOffset: '2px',
  },
});

const menuItemProps = {
  bg: 'transparent',
  _highlighted: {
    bg: 'transparent',
  },
};

export const MoreMenuIcon = ({ kind }: { kind: MoreMenuIconKind }) => {
  const iconProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    width: '1em',
    height: '1em',
    viewBox: '0 0 24 24',
    fill: 'none',
  };

  const icon =
    kind === 'stats' ? (
      <svg {...iconProps}>
        <path
          d="M4 19V5M4 19H21M8 16V10M12 16V7M16 16V12M20 16V4"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'bridge' ? (
      <svg {...iconProps}>
        <path
          d="M5 7H19M5 17H19M7 5L5 7L7 9M17 15L19 17L17 19M8 12H16"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'docs' ? (
      <svg {...iconProps}>
        <path
          d="M6 3H14L19 8V21H6V3Z"
          stroke="currentColor"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M14 3V8H19M9 13H16M9 17H14"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'portfolio' ? (
      <svg {...iconProps}>
        <path
          d="M4 8H20V19H4V8ZM8 8V6C8 4.89543 8.89543 4 10 4H14C15.1046 4 16 4.89543 16 6V8"
          stroke="currentColor"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M9 13H15"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'leaderboard' ? (
      <svg {...iconProps}>
        <path
          d="M8 21V10H16V21M3 21V14H8M16 21V6H21V21M10 6L12 3L14 6"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'help' ? (
      <svg {...iconProps}>
        <path
          d="M12 18H12.01M9.5 9.5C9.5 8.11929 10.6193 7 12 7C13.3807 7 14.5 8.11929 14.5 9.5C14.5 11.5 12 11.75 12 14"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'privacy' ? (
      <svg {...iconProps}>
        <path
          d="M12 3L19 6V11C19 15.4183 16.1348 19.1629 12 20.5C7.86523 19.1629 5 15.4183 5 11V6L12 3Z"
          stroke="currentColor"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M9.5 12L11.25 13.75L15 10"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    ) : kind === 'blog' ? (
      <svg {...iconProps}>
        <path
          d="M5 4H19V20H5V4Z"
          stroke="currentColor"
          strokeLinejoin="round"
          strokeWidth="2"
        />
        <path
          d="M8 8H16M8 12H16M8 16H13"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="1em"
        height="1em"
        viewBox="0 0 16 16"
        fill="currentColor"
        shapeRendering="crispEdges"
      >
        <g>
          <rect x="4" y="3" width="2" height="1" />
          <rect x="12" y="3" width="2" height="1" />
          <rect x="4" y="4" width="3" height="1" />
          <rect x="11" y="4" width="3" height="1" />
          <rect x="5" y="5" width="2" height="1" />
          <rect x="8" y="5" width="2" height="1" />
          <rect x="11" y="5" width="2" height="1" />
          <rect x="6" y="6" width="5" height="1" />
          <rect x="2" y="7" width="13" height="1" />
          <rect x="2" y="8" width="13" height="1" />
          <rect x="6" y="9" width="5" height="1" />
          <rect x="5" y="10" width="2" height="1" />
          <rect x="8" y="10" width="2" height="1" />
          <rect x="11" y="10" width="2" height="1" />
          <rect x="4" y="11" width="3" height="1" />
          <rect x="11" y="11" width="3" height="1" />
          <rect x="4" y="12" width="2" height="1" />
          <rect x="12" y="12" width="2" height="1" />
        </g>
      </svg>
    );

  return (
    <Icon boxSize="5" color="v2.text.muted">
      {icon}
    </Icon>
  );
};

const MenuEntryContent = ({
  icon,
  title,
  description,
}: {
  icon: MoreMenuIconKind;
  title: string;
  description: string;
}) => (
  <>
    <MoreMenuIcon kind={icon} />
    <Box>
      <Text textStyle="v2.ui" color="v2.text.primary" mb="3px">
        {title}
      </Text>
      <Text textStyle="v2.meta" color="v2.text.muted" lineHeight="1.35">
        {description}
      </Text>
    </Box>
  </>
);

export const TopMoreMenu: React.FC<BoxProps> = (props) => {
  const { t } = useTranslation();
  const { address } = useAccount();
  const showPrivacyPoolsMenu = isAddressInPrivacyPoolAllowlist(address);

  return (
    <Box h="full" {...props}>
      <Menu.Root
        lazyMount
        unmountOnExit
        positioning={{ placement: 'bottom-start' }}
      >
        <Menu.Trigger asChild>
          <styled.button
            h="full"
            px="14px"
            bg="transparent"
            border="none"
            cursor="pointer"
            className={css({
              outline: 'none',
              _focus: { outline: 'none' },
              _focusVisible: { outline: 'none' },
            })}
            // match TopTabs nav links: hover = text color only, no bg
            _hover={{
              color: 'v2.text.primary',
            }}
            color="v2.text.muted"
            transition="color 160ms ease"
          >
            <HStack gap="7px">
              <Text
                color="currentColor"
                textStyle="v2.ui"
                lineHeight="1"
              >
                {t('common.More', 'More')}
              </Text>
              <Menu.Context>
                {(ctx) => (
                  <Icon
                    boxSize="4"
                    transform={ctx.open ? 'rotate(180deg)' : ''}
                    transition="transform 0.2s ease"
                  >
                    <ArrowDown />
                  </Icon>
                )}
              </Menu.Context>
            </HStack>
          </styled.button>
        </Menu.Trigger>
        <Menu.Positioner>
          <Menu.Content
            bg="v2.bg.panel"
            border="1px solid"
            borderColor="v2.border.default"
            borderRadius="4px"
            boxShadow="modal"
            mt="10px"
            py={0}
            overflow="hidden"
            w="560px"
            maxW="calc(100vw - 24px)"
          >
            <Menu.ItemGroup maxH="70vh" overflow="auto" p={0} bg="v2.bg.panel">
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, minmax(0, 1fr))"
                columnGap="8px"
                rowGap="4px"
                px="10px"
                py="10px"
              >
                <Menu.Item value="portfolio" {...menuItemProps} asChild>
                  <NextLinkWithLocale
                    href={ROUTES.PORTFOLIO}
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="portfolio"
                      title={t('common.Portfolio', 'Portfolio')}
                      description={t(
                        'topMore.portfolioDescription',
                        'Review your balances and open positions',
                      )}
                    />
                  </NextLinkWithLocale>
                </Menu.Item>

                <Menu.Item value="docs" {...menuItemProps} asChild>
                  <styled.a
                    href={GIT_BOOK_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="docs"
                      title={t('common.Docs', 'Docs')}
                      description={t(
                        'topMore.docsDescription',
                        'Read official documentation and guides',
                      )}
                    />
                  </styled.a>
                </Menu.Item>

                <Menu.Item value="leaderboard" {...menuItemProps} asChild>
                  <NextLinkWithLocale
                    href={ROUTES.LEADERBOARD}
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="leaderboard"
                      title={t('common.Leaderboard', 'Leaderboard')}
                      description={t(
                        'topMore.leaderboardDescription',
                        'Compare trader performance across LeverUp',
                      )}
                    />
                  </NextLinkWithLocale>
                </Menu.Item>

                <Menu.Item value="dune" {...menuItemProps} asChild>
                  <styled.a
                    href={DUNE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="stats"
                      title={t('topMore.duneStats', 'Dune (Stats)')}
                      description={t(
                        'topMore.statsDescription',
                        'Platform-wide trading statistics',
                      )}
                    />
                  </styled.a>
                </Menu.Item>

                <Menu.Item value="bridge" {...menuItemProps} asChild>
                  <styled.a
                    href={MONAD_BRIDGE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="bridge"
                      title={t('common.Bridge', 'Bridge')}
                      description={t(
                        'topMore.bridgeDescription',
                        'Transfer assets across networks seamlessly',
                      )}
                    />
                  </styled.a>
                </Menu.Item>

                <Menu.Item value="blog" {...menuItemProps} asChild>
                  <styled.a
                    href={BLOG_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="blog"
                      title={t('common.Blog', 'Blog')}
                      description={t(
                        'topMore.blogDescription',
                        'Read LeverUp updates, market notes, and deep dives',
                      )}
                    />
                  </styled.a>
                </Menu.Item>

                {showPrivacyPoolsMenu ? (
                  <Menu.Item value="privacy-pools" {...menuItemProps} asChild>
                    <NextLinkWithLocale
                      href={ROUTES.PRIVACY_POOLS}
                      className={moreMenuLinkClass}
                    >
                      <MenuEntryContent
                        icon="privacy"
                        title={t('common.PrivacyPools', 'Privacy Pools')}
                        description={t(
                          'topMore.privacyPoolsDescription',
                          'Access private deposits and withdrawals',
                        )}
                      />
                    </NextLinkWithLocale>
                  </Menu.Item>
                ) : (
                  <Box />
                )}

                <Menu.Item value="anycollateral" {...menuItemProps} asChild>
                  <styled.a
                    href={ANYCOLLATERAL_PROGRAM_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={moreMenuLinkClass}
                  >
                    <MenuEntryContent
                      icon="anyCollateral"
                      title={t(
                        'topMore.anyCollateralProgram',
                        'AnyCollateral Program',
                      )}
                      description={t(
                        'topMore.anyCollateralProgramDescription',
                        'Apply to make your Monad token usable as LeverUp collateral',
                      )}
                    />
                  </styled.a>
                </Menu.Item>
              </Box>

              <Flex
                justifyContent="space-between"
                alignItems="center"
                px="16px"
                py="12px"
                gap="14px"
                bg="v2.bg.app"
                borderTop="1px solid"
                borderTopColor="v2.border.hairline"
              >
                <HStack gap="18px">
                  <styled.a
                    href={TERMS_CONDITION}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                    className={footerLinkClass}
                  >
                    <Text textStyle="v2.meta">
                      {t('common.Terms', 'Terms')}
                    </Text>
                    <Icon boxSize="3">
                      <LinkExternal />
                    </Icon>
                  </styled.a>
                  <styled.a
                    href={GIT_BOOK_AUDITS}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                    className={footerLinkClass}
                  >
                    <Text textStyle="v2.meta">
                      {t('common.Audits', 'Audits')}
                    </Text>
                    <Icon boxSize="3">
                      <LinkExternal />
                    </Icon>
                  </styled.a>
                </HStack>

                <HStack gap="12px">
                  <styled.a
                    href={TELEGRAM_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                    className={socialLinkClass}
                  >
                    <styled.img
                      src="/images/telegram.svg"
                      alt="telegram"
                      width="18px"
                      height="18px"
                    />
                  </styled.a>
                  <styled.a
                    href={DISCORD_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                    className={socialLinkClass}
                  >
                    <styled.img
                      src="/images/discord.svg"
                      alt="discord"
                      width="18px"
                      height="18px"
                    />
                  </styled.a>
                  <styled.a
                    href={X_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                    className={socialLinkClass}
                  >
                    <styled.img
                      src="/images/x.svg"
                      alt="x"
                      width="18px"
                      height="18px"
                    />
                  </styled.a>
                </HStack>
              </Flex>
            </Menu.ItemGroup>
          </Menu.Content>
        </Menu.Positioner>
      </Menu.Root>
    </Box>
  );
};
